import { useEffect } from 'react';
import { ory, clearSessionCache } from 'src/auth/ory';
import { redirectToLogin } from 'src/auth/RedirectToLoginPage';
import { useUserStore } from 'src/auth/useUserStore';
import { AUTH_BROADCAST_CHANNEL } from 'src/auth/authBroadcast';
import type { SessionData } from 'src/auth/SessionDataHook';

const VISIBILITY_CHECK_COOLDOWN_MS = 60_000;

export const useAuthSync = () => {
  useEffect(() => {
    const channel = new BroadcastChannel(AUTH_BROADCAST_CHANNEL);

    channel.onmessage = (event: MessageEvent<{ type: string }>) => {
      if (event.data?.type === 'logout') {
        clearSessionCache();
        redirectToLogin({ returnToPage: false });
      }
    };

    let lastCheckAt = 0;

    const handleVisibilityChange = async () => {
      if (document.visibilityState !== 'visible') return;

      const storedUserId = useUserStore.getState().userId;
      if (!storedUserId) return;

      const now = Date.now();
      if (now - lastCheckAt < VISIBILITY_CHECK_COOLDOWN_MS) return;
      lastCheckAt = now;

      try {
        const res = await ory.toSession();
        const freshUserId = (res.data as SessionData)?.identity?.metadata_public?.userId;

        // Someone else is logged in now, and `LoginPage` would send them straight on to this
        // user's page, which they likely can't open
        if (freshUserId !== storedUserId) {
          clearSessionCache();
          redirectToLogin({ returnToPage: false });
        }
      } catch {
        clearSessionCache();
        redirectToLogin();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      channel.close();
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);
};
